/**
 * Theme Definitions for Classrooms
 * Each theme has primary and secondary color scales
 */

export interface Theme {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
    secondary: {
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
  };
}

export const themes: Theme[] = [
  {
    id: 'sky',
    name: 'Sky Blue',
    description: 'The classrooms brand blue, matching the book mascot logo',
    colors: {
      primary: {
        50: '#f2f8ff',
        100: '#e3f0fe',
        200: '#c9e2fe',
        300: '#aed3fd',
        400: '#a0ccfd',
        500: '#93c5fd', // logo chip — the brand color
        600: '#7bb2f0',
        700: '#649cdd',
        800: '#4d7cb5',
        900: '#3d618c',
      },
      secondary: {
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#151e30',
        900: '#0d121f',
      },
    },
  },
  {
    id: 'indigo',
    name: 'Indigo',
    description: 'The classrooms brand indigo, matching the book mascot logo',
    colors: {
      primary: {
        50: '#f5f7ff',
        100: '#eef1fe',
        200: '#dfe5fd',
        300: '#c7d2fe',
        400: '#b6c3fd',
        500: '#a5b4fc', // logo chip — the brand color
        600: '#93a1f0',
        700: '#7b89dd',
        800: '#5f6bb5',
        900: '#4a548c',
      },
      secondary: {
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#151e30',
        900: '#0d121f',
      },
    },
  },
  {
    id: 'duolingo',
    name: 'Emerald',
    description: 'A refined, balanced green, the classrooms.work brand',
    colors: {
      primary: {
        50: '#ecfdf5',
        100: '#d1fae5',
        200: '#a7f3d0',
        300: '#6ee7b7',
        400: '#34d399',
        500: '#10b981',
        600: '#059669',
        700: '#047857',
        800: '#065f46',
        900: '#064e3b',
      },
      secondary: {
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
      },
    },
  },
  {
    id: 'terracotta',
    name: 'Warm Terracotta',
    description: 'Earthy and inviting with warm coral tones',
    colors: {
      primary: {
        50: '#fdf5f3',
        100: '#fce8e4',
        200: '#fbd3ca',
        300: '#f7b2a2',
        400: '#f08b73',
        500: '#e2725b',
        600: '#c95d48',
        700: '#a84b39',
        800: '#8c3f31',
        900: '#75362b',
      },
      secondary: {
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
      },
    },
  },
  {
    id: 'soft-pastels',
    name: 'Soft Pastels',
    description: 'Gentle and calming with blue and beige tones',
    colors: {
      primary: {
        50: '#f0f8fa',
        100: '#e1f1f5',
        200: '#d4e9ee',
        300: '#bbdce5', // User provided
        400: '#a3cdd9',
        500: '#8bbfcd',
        600: '#6fa9bb',
        700: '#5891a7',
        800: '#477789',
        900: '#385f6f',
      },
      secondary: {
        100: '#fcfcfb',
        200: '#f7f7f3',
        300: '#eceedf', // User provided
        400: '#e3e5d5',
        500: '#d9c4b0', // User provided
        600: '#cfab8d', // User provided
        700: '#b8936f',
        800: '#9a7957',
        900: '#7d6147',
      },
    },
  },
  {
    id: 'warm-neutrals',
    name: 'Warm Neutrals',
    description: 'Cozy and sophisticated with rose and sage',
    colors: {
      primary: {
        50: '#faf6f5',
        100: '#f3ebe9',
        200: '#ead9d5',
        300: '#dfc3bc',
        400: '#d6a99d', // User provided
        500: '#c8968a',
        600: '#b57d6f',
        700: '#9d6859',
        800: '#82544a',
        900: '#6a453c',
      },
      secondary: {
        100: '#fdfcf9',
        200: '#fbf3d5', // User provided
        300: '#edefd9',
        400: '#dfe3c8',
        500: '#d6dac8', // User provided
        600: '#bcc4b4',
        700: '#9cafaa', // User provided
        800: '#7d9289',
        900: '#647570',
      },
    },
  },
  {
    id: 'navy-blues',
    name: 'Navy Blues',
    description: 'Professional and crisp with navy and sky blue',
    colors: {
      primary: {
        50: '#f2f7fa',
        100: '#dceaf2',
        200: '#c4dce8',
        300: '#a4c9da',
        400: '#94b4c1', // User provided
        500: '#7ba2b3',
        600: '#547792', // User provided
        700: '#4a6681',
        800: '#3d5469',
        900: '#334557',
      },
      secondary: {
        100: '#fdfef9',
        200: '#f7f8ed',
        300: '#ecefca', // User provided
        400: '#dfe3b8',
        500: '#c8cfaa',
        600: '#a8b391',
        700: '#7a8872',
        800: '#4a5a5a',
        900: '#213448', // User provided (darkest)
      },
    },
  },
  {
    id: 'deep-blues',
    name: 'Deep Blues',
    description: 'Bold and elegant with navy and warm beige',
    colors: {
      primary: {
        50: '#f9f7f5',
        100: '#f5efe7', // User provided (lightest)
        200: '#ede5db',
        300: '#e2d8cc',
        400: '#d8c4b6', // User provided
        500: '#c7b0a0',
        600: '#b09384',
        700: '#947a69',
        800: '#786353',
        900: '#5f4f43',
      },
      secondary: {
        100: '#eef2f7',
        200: '#d9e1eb',
        300: '#b8c7d9',
        400: '#8fa5c2',
        500: '#6b87ab',
        600: '#527197',
        700: '#3e5879', // User provided
        800: '#2f4460',
        900: '#213555', // User provided (darkest)
      },
    },
  },
];

export const defaultTheme = themes.find(t => t.id === 'sky') || themes[0];

export function getThemeById(id: string): Theme {
  return themes.find(theme => theme.id === id) || defaultTheme;
}
